import {Badge, Icon, Modal, Drawer, Button, Row, Col, InputNumber, Form, notification} from 'antd';
import * as React from 'react';
import config from 'src/config';
import { searchableAttributes, unsearchableAttributes } from 'src/mappings';

import OpenSeadragon from './openseadragon';

export interface ImageViewProps {
  data: any;
  viewed: boolean;
  markAsViewed: (id: string) => void;
}

const promiseToWait = async (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export class ShowImages extends React.Component<ImageViewProps, any> {
  public state = {
    modalVisible: false,
    visible: false,
    printVisible: false,
    currentSelectedImage: 0,
    imageMeta: this.props.data && this.props.data.images ? this.props.data.images[0] : null,
    printable: false
  };

  paperSizeRef: any;
  qualityRef: any;

  processingNotification:string;
  successMessage:string;
  failMessage:string;

  constructor(props: ImageViewProps) {
    super(props);
    this.paperSizeRef = React.createRef();
    this.qualityRef = React.createRef();
    this.processingNotification = 'Image is being processed, please allow popup window, generated image will display in new tab/window.';
    this.successMessage = 'Image is generated, and opened in new tab/window. ';
    this.failMessage = 'Failed to process image, please try again later.';
  }

  showDrawer = () => {
    this.setState({
      visible: true,
    });
  };

  disableRightClick = (event: React.MouseEvent) => {
    event.preventDefault();
  };

  onClose = () => {
    this.setState({
      visible: false,
    });
  };

  onPrintImage = (imageIndex: number) => {
    this.setState({
      printVisible: true,
      currentSelectedImage: imageIndex
    })
  };

  cancelPrint = () => {
    this.setState({printVisible: false});
  };

  s3FetchWithRetry = async (s3SignedUrl: string, waitMs: number = 3 * 1000, timeoutMinutes = 5) => {

    let res: Response = await fetch(
      s3SignedUrl,
      {
        method: 'GET',
        redirect: 'follow'
      },
    );
    let resText: string = await res.clone().text()

    let requestCount: number = 1
    const maxRequestCount: number = (timeoutMinutes * 60 * 1000) / waitMs // number of retries based on timeoutMinutes

    // check the text to avoid parsing the XML message
    // retry 404s pass through any other responses
    while (res.status === 404 && resText.includes("The specified key does not exist") && requestCount < maxRequestCount) {
      await promiseToWait(waitMs) // wait 3 seconds
      res = await fetch(
        s3SignedUrl,
        {
          method: 'GET',
          redirect: 'follow'
        },
        
      );
      resText = await res.clone().text()
      requestCount++
    }

    return res;
  };


  confirmPrint = () => {
    this.cancelPrint();
    notification.info({
      message: 'Processing Image ...',
      key: 'processingNotification',
      description: this.processingNotification,
      duration: 0
    });

    const paperSize = this.paperSizeRef.current.value;
    const printQuality = this.qualityRef.current.value;
    const { imageName, collectionId } = this.props.data;
    const { imageUpdated, location, fileName, seqNo } = this.props.data.images[this.state.currentSelectedImage];
    const imagePath = `${location}/${fileName}`;
    const printDateAndTime = Date().toString();
    const jwtToken = sessionStorage.getItem('x-portal-token') ? sessionStorage.getItem('x-portal-token') + '' : '';
    const pageNumber = `${seqNo}/${this.props.data.images.length}`;
    const requestQueryParams = {
      imagePath,
      imageName,
      imageUpdated,
      collectionId,
      pageNumber,
      paperSize,
      printQuality,
      printDateAndTime,
      jwtToken
    };
    // call printAPI to get location of s3 PDF
    fetch(`${config.printAPI}?${new URLSearchParams(requestQueryParams).toString()}`,{
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      redirect: 'follow'
    })
    .then((resp) => 
    {
      if (resp.status >= 400) {
        throw new Error('error occurred for resize function');
      } else {
        return resp.json()
      }
    })
    // Get PDF from s3 using a signed link in location
    .then((data) => {
      return this.s3FetchWithRetry(data.location)
    })
    .then((resp) => {
      if (resp.status >= 400) {
        throw new Error('error occurred for print function');
      } else {
        return resp.blob();
      }
    })
    .then(data => {
      const file = new Blob([data], { type: 'application/pdf' });
      if(window.navigator.msSaveOrOpenBlob) {

        // IE11
        window.navigator.msSaveOrOpenBlob(file, 'lrs-image.pdf');
      } else {

        // Google chrome, Firefox, ....
        const fileURL = window.URL.createObjectURL(file);
        window.open(fileURL, '_blank');
      }
      notification.close('processingNotification');
      notification.success({
        message: 'Image generated successfully',
        description: this.successMessage,
        duration: 5
      });
    })
    .catch(e => {
      notification.close('processingNotification');
      notification.error({
        message: 'Error occurs!',
        description: this.failMessage,
        duration: 0
      });
      console.log('error occurred for print function:', e);
    });
  };

  onPageNumberInputChange  = (pageNumber: number) => {
    const targetImageIndex = pageNumber - 1;
    this.goToPage(targetImageIndex);
  };

  onPageChange = (imageIndex: number) => {
    const imageData: [] = this.props.data.images[imageIndex];
    this.setState({
      imageMeta: imageData,
      currentSelectedImage: imageIndex,
    });
  };

  goToPage = (imageIndex: number) => {};

  public render() {

    const visibleImages = this.props.data.images;
    if (visibleImages && visibleImages.length > 0) {

      const imageMetadataDisplayAttributes: any = [];

      const allAttributes = Object.assign({}, searchableAttributes, unsearchableAttributes);

      Object.keys(allAttributes).map((key)=>{

        const fieldName = allAttributes[key].FIELD_NAMES[0];
        const displayName = allAttributes[key].DISPLAY_NAME;

        if (this.props.data && this.props.data[fieldName] && fieldName != 'location') {
          const listItem =
            <Row key={key.toString()} style={{ fontSize: '0.9em' }}>
              <Col span={8}>
                <strong>{displayName + ': '} </strong>
              </Col>
              <Col span={12}>
                <span dangerouslySetInnerHTML={{ __html: this.props.data[fieldName] }}/>
              </Col>
            </Row>;
          imageMetadataDisplayAttributes.push(listItem)
        }
      });

      return (
        <div onContextMenu={this.disableRightClick} >
          <span>
          <a onClick={() => this.openImage()} title={'view image'}>
            <Badge count={visibleImages.length} style={{ backgroundColor: this.props.viewed? 'darkgrey': '#1890ff' }}>
              <Icon type='file-search' style={{ fontSize: '1.5em' }}/>
            </Badge>
          </a>
          <Modal
            className={'lightbox'}
            title={'Image Viewer'}
            visible={this.state.modalVisible}
            onCancel={() => this.setModalVisible(false)}
            width={'97vw'}
            bodyStyle={{ padding: 0 }}
            style={{ top: 10, height: '85vh' }}
            footer={[
              <span key='pageNumBegin'>Image </span>,
              <InputNumber
                key='pageNumInput'
                value={visibleImages[this.state.currentSelectedImage].seqNo}
                min={1}
                max={visibleImages.length}
                maxLength={4}
                onChange={this.onPageNumberInputChange}
                style={{width:'60px'}}
              />,
              <span key='pageNumEnd'> of {visibleImages.length}</span>
            ]}
          >
            <div
              style={{ width: '97vw', height: '85vh' }}
              id={'seadragon_container'}
            >
              <OpenSeadragon
                tileSources={this.getImageUrl(visibleImages)}
                showNavigationControl={true}
                showNavigator={true}
                showRotationControl={true}
                style={{ width: '97vw', height: '85vh' }}
                onPrint={this.onPrintImage}
                setPageChangeTrigger={(f: any) => this.goToPage = f}
                onPageChange={this.onPageChange}
                displayPrint={this.state.printable}
              />
              <Button htmlType='button' key='showMetaData' style={{margin:'10px'}} onClick={this.showDrawer}>Show image metadata</Button>
              <Modal
                closable={false}
                title='Print Settings'
                visible={this.state.printVisible}
                footer={[
                  <Button htmlType='button' key='cancel' onClick={this.cancelPrint}>Cancel</Button>,
                  <Button htmlType='button' key='print' onClick={this.confirmPrint}>Print</Button>
                ]}
              >
                <Form>
                  <Form.Item label='Paper Size:'>
                    <select
                      style={{ width: 200 }}
                      placeholder='Select Paper Size'
                      ref={this.paperSizeRef}
                    >
                      <option value='A4'>A4 (210 X 297MM)</option>
                      <option value='A3'>A3 (297 X 420MM)</option>
                    </select>
                  </Form.Item>
                  <Form.Item label='Print Quality'>
                    <select
                      ref={this.qualityRef}
                      style={{ width: 200 }}
                      placeholder='Select Print Quality'
                    >
                      <option value='72'>72 dpi</option>
                      <option value='96'>96 dpi</option>
                      <option value='150'>150 dpi</option>
                      <option value='300'>300 dpi</option>
                    </select>
                  </Form.Item>
                </Form>
              </Modal>
            </div>
          </Modal>
          <Drawer
            width={'580'}
            title='Image Metadata'
            placement='left'
            closable={true}
            onClose={this.onClose}
            visible={this.state.visible}
          >
            {imageMetadataDisplayAttributes}
            {this.state.imageMeta && this.state.imageMeta.fileName && (
              <Row style={{ fontSize: '0.9em' }}>
                <Col span={8}>
                  <strong>File Name: </strong>
                </Col>
                <Col span={12}>
                  <span dangerouslySetInnerHTML={{ __html: this.state.imageMeta.fileName }} />
                </Col>
              </Row>
            )}
            {this.state.imageMeta && this.state.imageMeta.fileSize && (
              <Row style={{ fontSize: '0.9em' }}>
                <Col span={8}>
                  <strong>File Size (KB): </strong>
                </Col>
                <Col span={12}>
                  <span dangerouslySetInnerHTML={{ __html: this.state.imageMeta.fileSize }} />
                </Col>
              </Row>
            )}
            {this.state.imageMeta && this.state.imageMeta.width && this.state.imageMeta.height && (
              <Row style={{ fontSize: '0.9em' }}>
                <Col span={8}>
                  <strong>Image Size in DPI: </strong>
                </Col>
                <Col span={12}>
                  <span>{this.state.imageMeta.width} x {this.state.imageMeta.height} (width x height)</span>
                </Col>
              </Row>
            )}
            {this.state.imageMeta && this.state.imageMeta.widthInPixel && this.state.imageMeta.heightInPixel && (
              <Row style={{ fontSize: '0.9em' }}>
                <Col span={8}>
                  <strong>Image Size in Pixels: </strong>
                </Col>
                <Col span={12}>
                  <span>{this.state.imageMeta.widthInPixel} x {this.state.imageMeta.heightInPixel} (width x height)</span>
                </Col>
              </Row>
            )}
            {this.state.imageMeta && this.state.imageMeta.imageUpdated && (
              <Row style={{ fontSize: '0.9em' }}>
                <Col span={8}>
                  <strong>Date Last Modified: </strong>
                </Col>
                <Col span={12}>
                  <span dangerouslySetInnerHTML={{ __html: this.state.imageMeta.imageUpdated }} />
                </Col>
              </Row>
            )}
          </Drawer>
        </span>
        </div>
      );
    } else {
      return <span>no images...</span>;
    }
  }

  private static buildSimpleTemplate(img: any) {
    const resourceUri = encodeURIComponent(`${img.location}/${img.fileName}`);
    return `${config.image.IMAGE_HOST}${resourceUri}/info.json`;
  }

  private getImageUrl(images: [any]): any {
    // return images.map(value => this.buildSimpleTemplate(value));
    return images.map(value => ShowImages.buildSimpleTemplate(value));
  }

  private setModalVisible(modalVisible: boolean) {
    this.setState({ modalVisible });
  }

  private openImage() {
    const requestBody = {collectionId: this.props.data.collectionId, jwt_token: sessionStorage.getItem('x-portal-token')};
    fetch(config.printRestrictionAPI,{
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })
    .then((resp) => resp.text())
    .then(data => {
      if (data === 'ALLOWED') {
        this.setState({printable: true});
      }
      this.setModalVisible(true);
      this.props.markAsViewed(this.props.data._id);
    })
    .catch(e => {
      console.log(`error occurred restricting print for collection ${this.props.data.collectionId}:`, e);
      this.setModalVisible(true);
    });
  }
}



// WEBPACK FOOTER //
// ./src/components/ShowImages.tsx